import { useState } from "react";
import axios from "axios";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATTERS = [
    "General Inquiry",
    "Executive & Ownership Advisory",
    "Compliance & Regulatory",
    "Operations & Performance",
    "Litigation & Risk Support",
    "Government Contracting",
];

const inputClass =
    "w-full border border-slate-800 bg-transparent px-4 py-3 text-sm text-slate-200 placeholder-slate-600 outline-none transition-colors duration-300 focus:border-blue-500";

const Label = ({ htmlFor, children }) => (
    <label htmlFor={htmlFor} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
        {children}
    </label>
);

export const InquiryForm = () => {
    const [form, setForm] = useState({ name: "", organization: "", email: "", phone: "", matter: "General Inquiry", message: "" });
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setError("");
        try {
            await axios.post(`${API}/inquiries`, {
                name: form.name,
                organization: form.organization || null,
                email: form.email,
                phone: form.phone || null,
                matter: form.matter,
                message: form.message,
            });
            setStatus("sent");
        } catch (err) {
            setStatus("error");
            setError("Your inquiry could not be sent. Please try again, or reach us directly by phone or email.");
        }
    };

    if (status === "sent") {
        return (
            <div data-testid="inquiry-success-message" className="flex h-full min-h-[24rem] flex-col justify-center border border-slate-800 bg-[#0b0f16] p-10">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Received</p>
                <h3 className="mt-5 font-serif text-3xl font-medium text-slate-100">Your inquiry is with a principal.</h3>
                <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-400">
                    Every message is read personally and held in strict confidence. Expect a
                    considered reply within two business days.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} data-testid="inquiry-form" className="border border-slate-800 bg-[#0b0f16] p-8 sm:p-10">
            <div className="grid gap-6 sm:grid-cols-2">
                <div>
                    <Label htmlFor="inquiry-name">Name *</Label>
                    <input id="inquiry-name" data-testid="inquiry-name-input" required minLength={2} maxLength={120}
                        value={form.name} onChange={update("name")} className={inputClass} placeholder="Full name" autoComplete="name" />
                </div>
                <div>
                    <Label htmlFor="inquiry-organization">Organization</Label>
                    <input id="inquiry-organization" data-testid="inquiry-organization-input" maxLength={160}
                        value={form.organization} onChange={update("organization")} className={inputClass} placeholder="Agency or organization" autoComplete="organization" />
                </div>
                <div>
                    <Label htmlFor="inquiry-email">Email *</Label>
                    <input id="inquiry-email" type="email" data-testid="inquiry-email-input" required
                        value={form.email} onChange={update("email")} className={inputClass} placeholder="you@agency.gov" autoComplete="email" />
                </div>
                <div>
                    <Label htmlFor="inquiry-phone">Phone</Label>
                    <input id="inquiry-phone" type="tel" data-testid="inquiry-phone-input" maxLength={40}
                        value={form.phone} onChange={update("phone")} className={inputClass} placeholder="(optional)" autoComplete="tel" />
                </div>
                <div className="sm:col-span-2">
                    <Label htmlFor="inquiry-matter">Nature of Inquiry</Label>
                    <select id="inquiry-matter" data-testid="inquiry-matter-select" value={form.matter} onChange={update("matter")}
                        className={`${inputClass} appearance-none bg-[#0b0f16]`}>
                        {MATTERS.map((m) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                </div>
                <div className="sm:col-span-2">
                    <Label htmlFor="inquiry-message">Message *</Label>
                    <textarea id="inquiry-message" data-testid="inquiry-message-textarea" required minLength={10} maxLength={4000} rows={6}
                        value={form.message} onChange={update("message")} className={`${inputClass} resize-y`}
                        placeholder="A brief description of the matter is sufficient. Please do not include case-sensitive details at this stage." />
                </div>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-slate-800 pt-8">
                <p className="max-w-sm text-xs leading-relaxed text-slate-500">
                    Submissions are confidential and reviewed only by a principal of the firm.
                </p>
                <button type="submit" data-testid="inquiry-submit-button" disabled={status === "sending"}
                    className="group inline-flex items-center gap-3 bg-slate-100 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-900 transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50">
                    {status === "sending" ? "Sending…" : "Send Inquiry"}
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
            </div>
            {status === "error" && (
                <p data-testid="inquiry-error-message" role="alert" className="mt-4 text-sm text-red-400">{error}</p>
            )}
        </form>
    );
};
